import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Typography, Box } from '@mui/material';
import Alert from '@mui/material/Alert';
import {
    getNetatmoData,
    getShellyData,
    getHeatpumpEnergyData,
    getHeatpumpSensoryData,
} from '../../api_calls/metabuild';

// Example data from documentation (fallback when API fails e.g. CORS)
const EXAMPLE_NETATMO = {
    '2025-07-01T01:54:11': { temperature: 27.6, co2: 887.0, humidity: 54.0 },
    '2025-07-01T03:55:28': { temperature: 27.6, co2: 1035.0, humidity: 54.0 },
    '2025-07-01T04:56:07': { temperature: 27.6, co2: 1100.0, humidity: 55.0 },
    '2025-07-01T06:00:00': { temperature: 27.2, co2: 950.0, humidity: 56.0 },
    '2025-07-01T08:30:00': { temperature: 28.1, co2: 820.0, humidity: 52.0 },
    '2025-07-01T12:00:00': { temperature: 29.0, co2: 750.0, humidity: 48.0 },
};
const EXAMPLE_SHELLY = [
    { timestamp: '01-07-2025 00:00', consumption: 0.1 },
    { timestamp: '01-07-2025 06:00', consumption: 0.3 },
    { timestamp: '01-07-2025 12:00', consumption: 0.5 },
    { timestamp: '01-07-2025 18:00', consumption: 0.4 },
    { timestamp: '02-07-2025 00:00', consumption: 0.2 },
    { timestamp: '02-07-2025 06:00', consumption: 0.35 },
    { timestamp: '02-07-2025 12:00', consumption: 0.45 },
];
const EXAMPLE_HEATPUMP_ENERGY = {
    '2025-07-01T02:00:00': { heating_consumption: 0.0, cooling_consumption: 0.0, consumption_sum: 0 },
    '2025-07-01T06:00:00': { heating_consumption: 1.2, cooling_consumption: 0.0, consumption_sum: 1.2 },
    '2025-07-01T10:00:00': { heating_consumption: 0.5, cooling_consumption: 0.0, consumption_sum: 0.5 },
    '2025-07-01T14:00:00': { heating_consumption: 0.0, cooling_consumption: 2.1, consumption_sum: 2.1 },
    '2025-07-01T18:00:00': { heating_consumption: 0.8, cooling_consumption: 0.0, consumption_sum: 0.8 },
};
const EXAMPLE_HEATPUMP_SENSORY = {
    '2025-07-01T01:00:05': { set_temperature: 28.0, outside_temperature: 28.0, inside_temperature: 28.9, mode: 'heating', status: 'off' },
    '2025-07-01T06:00:00': { set_temperature: 28.0, outside_temperature: 26.0, inside_temperature: 27.5, mode: 'heating', status: 'on' },
    '2025-07-01T12:00:00': { set_temperature: 24.0, outside_temperature: 32.0, inside_temperature: 26.2, mode: 'cooling', status: 'on' },
};

const MetabuildDeviceChart = ({ device }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!device?.isDemoDevice || !device?.metabuild_id) return;

        const fetchData = async () => {
            try {
                let result;
                if (device.device_type === 'netatmo') {
                    const res = await getNetatmoData(device.metabuild_id);
                    result = res.data;
                } else if (device.device_type === 'shelly') {
                    const res = await getShellyData(device.metabuild_id);
                    result = res.data;
                } else if (device.device_type === 'heatpump') {
                    const [energyRes, sensoryRes] = await Promise.all([
                        getHeatpumpEnergyData(device.metabuild_id),
                        getHeatpumpSensoryData(device.metabuild_id),
                    ]);
                    result = { energy: energyRes.data, sensory: sensoryRes.data };
                } else {
                    setData(null);
                    setLoading(false);
                    return;
                }

                if (result && (Object.keys(result).length > 0 || (Array.isArray(result) && result.length > 0))) {
                    console.log(`[METABUILD] ${device.name} (${device.device_type}, id=${device.metabuild_id}):`, result);
                    setData(result);
                } else {
                    throw new Error('Empty response');
                }
            } catch (err) {
                console.warn('Metabuild API failed, using example data:', err.message);
                const fallbackData = device.device_type === 'netatmo' ? EXAMPLE_NETATMO
                    : device.device_type === 'shelly' ? EXAMPLE_SHELLY
                        : device.device_type === 'heatpump' ? { energy: EXAMPLE_HEATPUMP_ENERGY, sensory: EXAMPLE_HEATPUMP_SENSORY }
                            : null;
                if (fallbackData) {
                    console.log(`[METABUILD] ${device.name} (${device.device_type}, id=${device.metabuild_id}) - fallback/example data:`, fallbackData);
                    setData(fallbackData);
                } else {
                    setData(null);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [device]);

    if (loading) {
        return <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>Loading...</Box>;
    }
    if (error) {
        return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
    }

    // Netatmo: temperature, co2, humidity
    if (device.device_type === 'netatmo' && data && typeof data === 'object' && !Array.isArray(data)) {
        const entries = Object.entries(data).sort((a, b) => a[0].localeCompare(b[0]));
        const labels = entries.map(([ts]) => ts.slice(5, 16));
        const temps = entries.map(([, v]) => v.temperature);
        const co2 = entries.map(([, v]) => v.co2);
        const humidity = entries.map(([, v]) => v.humidity);
        const latest = entries[entries.length - 1]?.[1] || {};

        const option = {
            xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 9 } },
            yAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#eee' } } },
            series: [
                { data: temps, type: 'line', smooth: true, name: '°C', lineStyle: { color: '#f44336' }, areaStyle: { color: '#f44336', opacity: 0.2 } },
                { data: co2, type: 'line', smooth: true, name: 'CO₂', lineStyle: { color: '#ff9800' }, areaStyle: { color: '#ff9800', opacity: 0.2 } },
                { data: humidity, type: 'line', smooth: true, name: '%', lineStyle: { color: '#2196f3' }, areaStyle: { color: '#2196f3', opacity: 0.2 } },
            ],
            grid: { top: 25, bottom: 45, left: 45, right: 10 },
            tooltip: { trigger: 'axis' },
            legend: { bottom: 0, itemGap: 8 },
        };

        return (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Temperature, CO₂, Humidity</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="caption"><strong>{latest.temperature}°C</strong></Typography>
                        <Typography variant="caption"><strong>{latest.co2} ppm</strong></Typography>
                        <Typography variant="caption"><strong>{latest.humidity}%</strong></Typography>
                    </Box>
                </Box>
                <ReactECharts option={option} style={{ height: 150, width: '100%' }} />
            </Box>
        );
    }

    // Shelly: consumption
    if (device.device_type === 'shelly' && data && Array.isArray(data)) {
        const labels = data.map((d) => d.timestamp);
        const consumption = data.map((d) => d.consumption);
        const total = consumption.reduce((s, v) => s + v, 0).toFixed(2);

        const option = {
            xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 9 } },
            yAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#eee' } } },
            series: [{
                data: consumption,
                type: 'line',
                smooth: true,
                areaStyle: { color: '#2196f3', opacity: 0.3 },
                lineStyle: { color: '#2196f3', width: 2 },
            }],
            grid: { top: 25, bottom: 30, left: 45, right: 10 },
            tooltip: { trigger: 'axis', formatter: '{b}<br/>{c} kWh' },
        };

        return (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Consumption (kWh)</Typography>
                    <Typography variant="subtitle2" fontWeight="bold" color="#2196f3">Total: {total} kWh</Typography>
                </Box>
                <ReactECharts option={option} style={{ height: 150, width: '100%' }} />
            </Box>
        );
    }

    // Heatpump: energy + sensory
    if (device.device_type === 'heatpump' && data?.energy && data?.sensory) {
        const energyEntries = Object.entries(data.energy).sort((a, b) => a[0].localeCompare(b[0]));
        const sensoryEntries = Object.entries(data.sensory).sort((a, b) => a[0].localeCompare(b[0]));
        const labels = energyEntries.map(([ts]) => ts.slice(5, 16));
        const consumption = energyEntries.map(([, v]) => v.consumption_sum ?? 0);
        const total = consumption.reduce((s, v) => s + v, 0).toFixed(2);
        const latestSensory = sensoryEntries[sensoryEntries.length - 1]?.[1];

        const option = {
            xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 9 } },
            yAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#eee' } } },
            series: [{
                data: consumption,
                type: 'line',
                smooth: true,
                areaStyle: { color: '#4caf50', opacity: 0.3 },
                lineStyle: { color: '#4caf50', width: 2 },
            }],
            grid: { top: 25, bottom: 30, left: 45, right: 10 },
            tooltip: { trigger: 'axis', formatter: '{b}<br/>{c} kWh' },
        };

        return (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Energy consumption</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption"><strong>{total} kWh</strong></Typography>
                        {latestSensory && (
                            <>
                                <Typography variant="caption">In: {latestSensory.inside_temperature}°C</Typography>
                                <Typography variant="caption">Out: {latestSensory.outside_temperature}°C</Typography>
                                <Typography variant="caption">{latestSensory.mode}</Typography>
                            </>
                        )}
                    </Box>
                </Box>
                <ReactECharts option={option} style={{ height: 150, width: '100%' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="caption" color="text.secondary">Metabuild ID: {device.metabuild_id}</Typography>
            <Box sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">No data</Typography>
            </Box>
        </Box>
    );
};

export default MetabuildDeviceChart;
